import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationModalComponent } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { TableComponent } from '../../../shared/components/table/table.component';
import { AlertService } from '../../../shared/services/alert.service';
import { EmailService } from '../../../shared/services/email.service';
import { LoadingService } from '../../../shared/services/loading.service';
import { IEmailLogs } from '../../../utils/interfaces/email-log';
import { ITableHeader } from '../../../utils/interfaces/table-header';
import {
  IPaginationValue,
  PaginationComponent,
} from './../../../shared/components/pagination/pagination.component';
import { EmailDetailsComponent } from './email-details/email-details.component';

@Component({
  selector: 'app-email-sending',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PaginationComponent,
    TableComponent,
  ],
  templateUrl: './email-sending.component.html',
  styleUrl: './email-sending.component.scss',
})
export class EmailSendingComponent implements OnInit, OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly alertService = inject(AlertService);
  private readonly emailService = inject(EmailService);
  private readonly loadingService = inject(LoadingService);
  private readonly dialog = inject(MatDialog);
  private readonly unsubscribe$ = new Subject();
  searchText = new FormControl('');

  pagination = {
    page: 1,
    take: 10,
  };

  itemCount = 0;

  tableHeader: ITableHeader[] = [
    'Frequência,frequencyName',
    'Data de envio,sendDate',
    'Falhou,countFailed',
    'Enviado,countSuccess',
    'Total,total',
    'Ações',
  ].map((item) => {
    const [description, propertyKey] = item.split(',');

    return {
      description: description,
      propertyKey: propertyKey ?? '',
      status: false,
      date: propertyKey === 'sendDate',
      actions: description === 'Ações',
      filter: false,
      showHour: false,
    };
  });
  actionList = ['Ver falhas', 'Ver enviados'];

  list: IEmailLogs[] = [];

  ngOnInit(): void {
    const { data, count } = this.activatedRoute.snapshot.data['logs'];
    this.itemCount = count ?? 0;
    this.list = data;

    this.searchText.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .pipe(debounceTime(700))
      .subscribe(() => this.getData());
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  getData(): void {
    this.loadingService.showLoading();

    this.emailService
      .getScheduledEmailLogs(
        !!this.searchText.value,
        this.searchText.value ?? '',
        this.pagination.page,
        this.pagination.take
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.list = res.data;
          this.itemCount = res.count ?? 0;
          this.loadingService.closeLoading();
        },
      });
  }

  onSelectAction(event: { id: string; func: string }): void {
    if (event.func === 'Ver falhas') {
      this.openEmailDetails(true, event.id);
    } else {
      this.openEmailDetails(false, event.id);
    }
  }

  openEmailDetails(failed: boolean, id: string): void {
    const dialogRef = this.dialog.open(EmailDetailsComponent, {
      minWidth: '1000px',
      data: {
        failed,
        log: this.list.find((log) => log.id === id),
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        if (!res) return;
        this.emailSendingConfirmation(res);
      });
  }

  emailSendingConfirmation(res: string): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '500px',
      data: {
        title: 'Reenvio de e-mail',
        description:
          'Tem certeza de que deseja reenviar? Certifique-se de ter revisado se há e-mails inativos.',
        btnCancel: 'Cancelar',
        btnConfirm: 'Sim',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        if (!result) return;

        this.retryEmail(res);
      });
  }

  retryEmail(id: string): void {
    this.loadingService.showLoading();

    this.emailService
      .retryEmail(id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.loadingService.closeLoading();
          this.alertService.success('E-mail reenviado com sucesso!');
        },
      });
  }

  onPaginationChange(event: IPaginationValue): void {
    this.pagination = event;

    this.getData();
  }
}
